/**
 * Money helpers. The API sends decimals as strings ("1250000.5"); we never convert them to JS numbers
 * (precision loss above 2^53 and binary fractions). Formatting splits the string and groups the integer
 * part with BigInt, so "999999999999999999.99" stays exact.
 */
export type DecimalString = string;

const DECIMAL_RE = /^-?\d+(\.\d+)?$/;

const CURRENCY_SYMBOL: Record<string, string> = { VND: '₫', USD: 'US$', EUR: '€' };
const CURRENCY_FRACTION: Record<string, number> = { VND: 0, USD: 2, EUR: 2 };

export function isDecimalString(value: unknown): value is DecimalString {
  return typeof value === 'string' && DECIMAL_RE.test(value.trim());
}

const groupFmt = new Intl.NumberFormat('vi-VN', { useGrouping: true, maximumFractionDigits: 0 });

function groupInteger(intPart: string): string {
  try {
    return groupFmt.format(BigInt(intPart || '0'));
  } catch {
    return intPart;
  }
}

/**
 * Formats a decimal string with vi-VN separators: "1250000.5" → "1.250.000,5".
 * `minFraction`/`maxFraction` pad or round (half-up on the string, no float math).
 */
export function formatDecimal(
  value: DecimalString | null | undefined,
  opts: { minFraction?: number; maxFraction?: number } = {},
): string {
  if (value === null || value === undefined || value === '') return '';
  const v = String(value).trim();
  if (!DECIMAL_RE.test(v)) return v;
  const { minFraction = 0, maxFraction = 4 } = opts;
  const negative = v.startsWith('-');
  let [intPart = '0', frac = ''] = (negative ? v.slice(1) : v).split('.');

  if (frac.length > maxFraction) {
    const roundUp = Number(frac[maxFraction]) >= 5;
    frac = frac.slice(0, maxFraction);
    if (roundUp) {
      const scaled = BigInt(intPart + frac) + 1n;
      const s = scaled.toString().padStart(maxFraction + 1, '0');
      intPart = maxFraction > 0 ? s.slice(0, -maxFraction) : s;
      frac = maxFraction > 0 ? s.slice(-maxFraction) : '';
    }
  }
  frac = frac.replace(/0+$/, '');
  if (frac.length < minFraction) frac = frac.padEnd(minFraction, '0');
  const isZero = /^0*$/.test(intPart) && /^0*$/.test(frac);
  const out = groupInteger(intPart) + (frac ? `,${frac}` : '');
  return negative && !isZero ? `-${out}` : out;
}

/** "1250000" + "VND" → "1.250.000 ₫". Null/empty → "—". */
export function formatMoney(value: DecimalString | null | undefined, currency = 'VND'): string {
  if (value === null || value === undefined || value === '') return '—';
  const fraction = CURRENCY_FRACTION[currency] ?? 2;
  const body = formatDecimal(value, { minFraction: currency === 'VND' ? 0 : fraction, maxFraction: currency === 'VND' ? 2 : fraction });
  const symbol = CURRENCY_SYMBOL[currency] ?? currency;
  return `${body}\u00a0${symbol}`;
}

/**
 * Parses what a user typed (vi-VN: "." thousands, "," decimal) into a plain decimal string.
 * "1.250.000" → "1250000", "1.250.000,50" → "1250000.50", "" → "". Invalid → null.
 */
export function parseMoneyInput(text: string, maxFraction = 2): DecimalString | null {
  const t = text.replace(/[\s\u00a0₫]/g, '').replace(/[A-Za-z$€]/g, '');
  if (t === '' || t === '-') return '';
  if (!/^-?[\d.]*(,\d*)?$/.test(t)) return null;
  const negative = t.startsWith('-');
  const [intRaw = '', fracRaw] = (negative ? t.slice(1) : t).split(',');
  const intDigits = intRaw.replace(/\./g, '').replace(/^0+(?=\d)/, '');
  const frac = (fracRaw ?? '').slice(0, maxFraction);
  if (intDigits === '' && frac === '') return '';
  const result = `${intDigits || '0'}${fracRaw !== undefined && frac !== '' ? `.${frac}` : ''}`;
  return negative ? `-${result}` : result;
}

/** Formats a plain decimal string for an input box while the user edits (keeps a trailing comma). */
export function formatMoneyInput(value: DecimalString | null | undefined, keepTrailingComma = false): string {
  if (value === null || value === undefined || value === '') return '';
  const [i = '0', f] = value.split('.');
  const negative = i.startsWith('-');
  const grouped = groupInteger(negative ? i.slice(1) : i);
  const out = `${negative ? '-' : ''}${grouped}${f !== undefined ? `,${f}` : keepTrailingComma ? ',' : ''}`;
  return out;
}

/** Exact comparison of two decimal strings: -1 | 0 | 1. */
export function compareDecimal(a: DecimalString, b: DecimalString): number {
  const scale = (s: string) => {
    const neg = s.startsWith('-');
    const [i = '0', f = ''] = (neg ? s.slice(1) : s).split('.');
    return { neg, i, f };
  };
  const x = scale(a);
  const y = scale(b);
  const len = Math.max(x.f.length, y.f.length);
  const bx = BigInt(x.i + x.f.padEnd(len, '0')) * (x.neg ? -1n : 1n);
  const by = BigInt(y.i + y.f.padEnd(len, '0')) * (y.neg ? -1n : 1n);
  return bx === by ? 0 : bx > by ? 1 : -1;
}

/** Exact sum of decimal strings (for client-side totals that are display-only; the server stays authoritative). */
export function sumDecimals(values: (DecimalString | null | undefined)[]): DecimalString {
  const parts = values.filter(isDecimalString);
  const scale = Math.max(0, ...parts.map((p) => (p.split('.')[1] ?? '').length));
  let total = 0n;
  for (const p of parts) {
    const neg = p.startsWith('-');
    const [i = '0', f = ''] = (neg ? p.slice(1) : p).split('.');
    const n = BigInt(i + f.padEnd(scale, '0'));
    total += neg ? -n : n;
  }
  const neg = total < 0n;
  const s = (neg ? -total : total).toString().padStart(scale + 1, '0');
  const result = scale > 0 ? `${s.slice(0, -scale)}.${s.slice(-scale)}`.replace(/\.?0+$/, '') : s;
  return neg ? `-${result}` : result;
}
